/**
 * DIMENSION measurement-value formatting — the pure, DXF-style-table-independent numeric
 * formatting core of DIMLFAC/DIMRND/DIMLUNIT/DIMDEC (T1 arm (c) round-22).
 *
 * ONE TRUTH shared by TWO independent consumers that each need "format a measured number the
 * way AutoCAD's DIMVARs say to":
 *   - `apps/web/src/app/scene/dimensionHelpers.ts::formatDimMeasurement` — the RENDER path's
 *     measurement-text formatter (feeds the actual on-screen DIMENSION text primitive).
 *   - `packages/drawing-model/src/wcsBoundsLeaf.ts::formattedMeasurementText` — the WCS-bounds
 *     path's width-estimate formatter (needs the SAME string the renderer draws, to size the
 *     text box correctly for an empty/`"<>"` `dimensionText`).
 *
 * These two functions were independently re-derived (`apps/web` cannot be imported FROM
 * `packages/drawing-model` — wrong dependency direction — so the bounds path could not import
 * the render module's copy directly) and jscpd flagged the resulting near-identical
 * switch-statement bodies as duplication. Since this formatting logic itself has ZERO
 * apps/web-specific dependencies (no React, no DOM, no i18n locale, no style-table lookup beyond
 * the raw numeric DIMVAR values already resolved by each caller) — pure number-in, string-out —
 * `apps/web` DEPENDS ON `packages/drawing-model` (the normal app→package direction
 * `.dependency-cruiser.cjs` allows), so THIS is the correct direction to converge on: defined
 * once here, `dimensionHelpers.ts` imports it instead of re-deriving (mirrors this package's own
 * established `mapBlockAttributeReferences`/`splineLeaderCubics` convergence precedent — see
 * `index.ts`'s re-export comments for those).
 */

/**
 * Round `value` to the nearest multiple of `dimrnd` (DXF group 45, DIMRND) — `dimrnd <= 0`
 * disables rounding (AutoCAD's documented "0 = no rounding" convention).
 */
export function applyDimRounding(value: number, dimrnd: number): number {
  if (dimrnd <= 0) return value;
  return Math.round(value / dimrnd) * dimrnd;
}

/**
 * Format `value` per the DXF DIMLUNIT (group 277) unit-system code, at DIMDEC (group 271)
 * decimal places: 1=Scientific, 2=Decimal (default), 3=Engineering (feet-inches decimal),
 * 4=Architectural (feet-inches fractional), 5=Fractional (simple fraction, denominator
 * `2^min(dimdec,8)`).
 */
export function formatDimValueByUnit(value: number, dimlunit: number, dimdec: number): string {
  switch (dimlunit) {
    case 1: // Scientific
      return value.toExponential(dimdec);
    case 3: {
      // Engineering (feet-inches decimal)
      const feet = Math.floor(value / 12);
      const inches = value - feet * 12;
      return `${feet}'-${inches.toFixed(dimdec)}"`;
    }
    case 4: {
      // Architectural (feet-inches fractional)
      const archFeet = Math.floor(value / 12);
      const archInches = Math.floor(value - archFeet * 12);
      const fraction = value - archFeet * 12 - archInches;
      if (fraction < 0.01) return `${archFeet}'-${archInches}"`;
      return `${archFeet}'-${archInches} ${fraction.toFixed(dimdec)}"`;
    }
    case 5: {
      // Fractional — approximate as a simple fraction with denominator 2^dimdec.
      const whole = Math.floor(value);
      const frac = value - whole;
      if (frac < 0.001) return String(whole);
      const denom = Math.pow(2, Math.min(dimdec, 8));
      const numer = Math.round(frac * denom);
      return whole > 0 ? `${whole} ${numer}/${denom}` : `${numer}/${denom}`;
    }
    case 2: // Decimal (default)
    default:
      return value.toFixed(dimdec);
  }
}
